import * as React from "react";
import { styled, useTheme, type Theme } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import type {} from "@mui/material/themeCssVarsAugmentation";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { Link } from "../shared/Link.tsx";
import { BrandingContext, NavigationContext } from "../shared/context.ts";
import { Account } from "../Account/Account.tsx";
import {
  canApplyFilter,
  getItemTitle,
  getPageItemFullPath,
  hasSelectedNavigationChildren,
  isPageItemSelected,
} from "../shared/navigation.tsx";
import { useApplicationTitle } from "../shared/branding.ts";
import { DashboardLayoutProps, Navigation } from "../../types/types.ts";
import {
  Outlet,
  useLocation,
  useNavigate,
  useMatches,
  Link as RouterLink,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserApplicationIdentifiers } from "../../redux/selectors/authSelectors.ts";
import { Breadcrumbs, Link as LinkMUI, Typography } from "@mui/material";
import { RootReducerState } from "../../utils/types.ts";

const LogoContainer = styled("div")({
  position: "relative",
  width: 50,
  height: 50,
  "& img": {
    maxHeight: 50,
    maxWidth: 50,
  },
});

const getDrawerSxTransitionMixin = (isExpanded: boolean, property: string) => ({
  transition: (theme: Theme) =>
    theme.transitions.create(property, {
      easing: theme.transitions.easing.sharp,
      duration: isExpanded
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
});

const getDrawerWidthTransitionMixin = (isExpanded: boolean) => ({
  ...getDrawerSxTransitionMixin(isExpanded, "width"),
  overflowX: "hidden",
});

interface DashboardSidebarSubNavigationProps {
  subNavigation: Navigation;
  basePath?: string;
  depth?: number;
  onLinkClick: () => void;
  isMini?: boolean;
  isFullyExpanded?: boolean;
  isMobileViewport?: boolean;
  selectedItemId: string;
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    width: "fit-content",
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

function DashboardSidebarSubNavigation({
  subNavigation,
  basePath = "",
  depth = 0,
  onLinkClick,
  isMini = false,
  isFullyExpanded = true,
  isMobileViewport = false,
  selectedItemId,
}: DashboardSidebarSubNavigationProps) {
  const navigate = useNavigate();
  const userState = useSelector((state: RootReducerState) => state.user);

  const location = useLocation();
  const pathname = location.pathname;
  const initialExpandedSidebarItemIds = React.useMemo(
    () =>
      subNavigation
        .map((navigationItem, navigationItemIndex) => ({
          navigationItem,
          originalIndex: navigationItemIndex,
        }))
        .filter(({ navigationItem }) =>
          hasSelectedNavigationChildren(navigationItem, basePath, pathname),
        )
        .map(({ originalIndex }) => `${depth}-${originalIndex}`),
    [basePath, depth, pathname, subNavigation],
  );

  const [expandedSidebarItemIds, setExpandedSidebarItemIds] = React.useState(
    initialExpandedSidebarItemIds,
  );

  const handleOpenFolderClick = React.useCallback(
    (itemId: string) => () => {
      setExpandedSidebarItemIds((previousValue) =>
        previousValue.includes(itemId)
          ? previousValue.filter(
              (previousValueItemId) => previousValueItemId !== itemId,
            )
          : [...previousValue, itemId],
      );
    },
    [],
  );

  const handleExternalLinkClick = React.useCallback(
    (navigationItem: any) => {
      // Access Redux store directly based on the segment
      if (
        navigationItem.segment === "sharepoint" &&
        userState.userClientHubUrl
      ) {
        window.open(
          userState.userClientHubUrl,
          "_blank",
          "noopener,noreferrer",
        );
      } else if (
        navigationItem.segment === "powerbi" &&
        userState.userClientInsightsUrl
      ) {
        window.open(
          userState.userClientInsightsUrl,
          "_blank",
          "noopener,noreferrer",
        );
      }
    },
    [userState],
  );

  return (
    <List sx={{ padding: 0, mb: depth === 0 ? 4 : 1, pl: 2 * depth }}>
      {subNavigation
        .filter((navigationItem) => !(navigationItem as any).hidden)
        .map((navigationItem, navigationItemIndex) => {
          if (navigationItem.kind === "header") {
            return (
              <ListSubheader
                key={`subheader-${depth}-${navigationItemIndex}`}
                component="div"
                sx={{
                  fontSize: 12,
                  fontWeight: "700",
                  height: isMini ? 0 : 40,
                  ...(isMobileViewport
                    ? {}
                    : getDrawerSxTransitionMixin(isFullyExpanded, "height")),
                  px: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getItemTitle(navigationItem)}
              </ListSubheader>
            );
          }
          if (navigationItem.kind === "divider") {
            const nextItem = subNavigation[navigationItemIndex + 1];

            return (
              <Divider
                key={`divider-${depth}-${navigationItemIndex}`}
                sx={{
                  borderBottomWidth: 2,
                  mx: 1,
                  mt: 1,
                  mb: nextItem?.kind === "header" && !isMini ? 0 : 1,
                  ...(isMobileViewport
                    ? {}
                    : getDrawerSxTransitionMixin(isFullyExpanded, "margin")),
                }}
              />
            );
          }

          const navigationItemFullPath = getPageItemFullPath(
            basePath,
            navigationItem,
          );
          const navigationItemId = `${depth}-${navigationItemIndex}`;
          const navigationItemTitle = getItemTitle(navigationItem);

          const isNestedNavigationExpanded =
            expandedSidebarItemIds.includes(navigationItemId);

          const nestedNavigationCollapseIcon = isNestedNavigationExpanded ? (
            <ExpandMoreIcon />
          ) : (
            <ExpandLessIcon sx={{ transform: "rotate(90deg)" }} />
          );

          const isSelected = isPageItemSelected(
            navigationItem,
            basePath,
            pathname,
          );

          if (
            process.env.NODE_ENV !== "production" &&
            isSelected &&
            selectedItemId
          ) {
            console.warn(
              `Duplicate selected path in navigation: ${navigationItemFullPath}`,
            );
          }

          if (isSelected && !selectedItemId) {
            selectedItemId = navigationItemId;
          }

          const listItem = (
            <ListItem
              sx={{
                py: 0,
                px: 1,
                overflowX: "hidden",
              }}
            >
              <ListItemButton
                selected={isSelected}
                sx={{
                  px: 1.4,
                  height: 48,
                }}
                onClick={() => {
                  if (navigationItem.externalRedirect) {
                    handleExternalLinkClick(navigationItem);
                  } else {
                    navigate(navigationItemFullPath!);
                    onLinkClick();
                  }
                }}
              >
                {navigationItem.icon || isMini ? (
                  <ListItemIcon>
                    {navigationItem.icon ?? null}
                    {!navigationItem.icon && isMini ? (
                      <Avatar>
                        {navigationItemTitle
                          .split(" ")
                          .slice(0, 2)
                          .map((itemTitleWord) =>
                            itemTitleWord.charAt(0).toUpperCase(),
                          )}
                      </Avatar>
                    ) : null}
                  </ListItemIcon>
                ) : null}
                <ListItemText primary={navigationItemTitle} />

                {/* Display action button (e.g., OpenInNew icon) if present */}
                {navigationItem.action && !isMini && isFullyExpanded
                  ? navigationItem.action
                  : null}

                {/* Show expand arrow only for items with children */}
                {navigationItem.children && !isMini && isFullyExpanded && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent parent navigation
                      handleOpenFolderClick(navigationItemId)();
                    }}
                  >
                    {isNestedNavigationExpanded ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ExpandLessIcon sx={{ transform: "rotate(90deg)" }} />
                    )}
                  </IconButton>
                )}
              </ListItemButton>
            </ListItem>
          );

          return (
            <React.Fragment key={navigationItemId}>
              {isMini ? (
                <CustomTooltip title={navigationItemTitle} placement="right">
                  {listItem}
                </CustomTooltip>
              ) : (
                listItem
              )}

              {navigationItem.children && !isMini ? (
                <Collapse
                  in={isNestedNavigationExpanded}
                  timeout="auto"
                  unmountOnExit
                >
                  <DashboardSidebarSubNavigation
                    subNavigation={navigationItem.children}
                    basePath={navigationItemFullPath}
                    depth={depth + 1}
                    onLinkClick={onLinkClick}
                    selectedItemId={selectedItemId}
                    isMobileViewport={isMobileViewport}
                  />
                </Collapse>
              ) : null}
            </React.Fragment>
          );
        })}
    </List>
  );
}

/**
 *
 * Demos:
 *
 * - [Dashboard Layout](https://mui.com/toolpad/core/react-dashboard-layout/)
 *
 * API:
 *
 * - [DashboardLayout API](https://mui.com/toolpad/core/api/dashboard-layout)
 */
function DashboardLayout(props: DashboardLayoutProps) {
  const { disableCollapsibleSidebar = false, slots, slotProps } = props;

  const theme = useTheme();

  const location = useLocation();
  const isDashboard = location.pathname === "/";

  //getCurrentPageName
  const matches = useMatches();
  const currentMatch = matches[matches.length - 1];
  const currentPageName = (currentMatch?.handle as any)?.title ?? "";

  const branding = React.useContext(BrandingContext);
  const applications = useSelector(getUserApplicationIdentifiers);
  const navigation = React.useContext(NavigationContext).filter((item) =>
    canApplyFilter(item, applications),
  );
  const applicationTitle = useApplicationTitle();

  //track last visited page to set sidebar state
  const prevPathRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const currentPath = location.pathname + location.search;

    if (prevPathRef.current && prevPathRef.current !== currentPath) {
      sessionStorage.setItem("lastVisitedPath", prevPathRef.current);
    }

    prevPathRef.current = currentPath;
  }, [location.pathname, location.search]);

  // const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] =
  //   React.useState(true);

  const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] =
    React.useState(isDashboard);

  React.useEffect(() => {
    setIsDesktopNavigationExpanded(isDashboard);
  }, [isDashboard]);

  const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] =
    React.useState(false);

  const isMobileViewport = useMediaQuery(theme.breakpoints.down("md"));

  const isNavigationExpanded = isDesktopNavigationExpanded;

  const setIsNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => setIsDesktopNavigationExpanded(newExpanded),
    [],
  );

  const [isNavigationFullyExpanded, setIsNavigationFullyExpanded] =
    React.useState(isNavigationExpanded);

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (isNavigationExpanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsNavigationFullyExpanded(true);
      }, theme.transitions.duration.enteringScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    setIsNavigationFullyExpanded(false);
  }, [isNavigationExpanded, theme]);

  const selectedItemIdRef = React.useRef("");

  // const toggleNavigationExpanded = React.useCallback(() => {
  //   setIsNavigationExpanded(!isNavigationExpanded);
  // }, [isNavigationExpanded, setIsNavigationExpanded]);

  const handleNavigationLinkClick = React.useCallback(() => {
    selectedItemIdRef.current = "";
    setIsMobileNavigationExpanded(false);
  }, [setIsMobileNavigationExpanded]);

  // If useEffect was used, the reset would also happen on the client render after SSR which we don't need
  React.useMemo(() => {
    selectedItemIdRef.current = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  const isDesktopMini =
    !disableCollapsibleSidebar && !isDesktopNavigationExpanded;

  // const getMenuIcon = React.useCallback(
  //   (isExpanded: boolean) => {
  //     const expandMenuActionText = "Expand";
  //     const collapseMenuActionText = "Collapse";

  //     return (
  //       <Tooltip
  //         title={`${isExpanded ? collapseMenuActionText : expandMenuActionText} menu`}
  //         enterDelay={500}
  //       >
  //         <div>
  //           <IconButton
  //             aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} navigation menu`}
  //             onClick={toggleNavigationExpanded}
  //           >
  //             {isExpanded ? (
  //               <MenuOpenIcon
  //                 sx={{ color: "#FFF", height: "25px", width: "25px" }}
  //               />
  //             ) : (
  //               <MenuIcon
  //                 sx={{ color: "#FFF", height: "25px", width: "25px" }}
  //               />
  //             )}
  //           </IconButton>
  //         </div>
  //       </Tooltip>
  //     );
  //   },
  //   [toggleNavigationExpanded],
  // );

  const getDrawerContent = React.useCallback(
    (isMini: boolean) => (
      <React.Fragment>
        <Box
          component="nav"
          sx={{
            marginTop: "70px",
            overflow: "auto",
            pt: navigation[0]?.kind === "header" && !isMini ? 0 : 2,
            ...(isMobileViewport
              ? {}
              : getDrawerSxTransitionMixin(
                  isNavigationFullyExpanded,
                  "padding",
                )),
          }}
        >
          <DashboardSidebarSubNavigation
            subNavigation={navigation}
            onLinkClick={handleNavigationLinkClick}
            isMini={isMini}
            isFullyExpanded={isNavigationFullyExpanded}
            isMobileViewport={isMobileViewport}
            selectedItemId={selectedItemIdRef.current}
          />
        </Box>
      </React.Fragment>
    ),
    [
      handleNavigationLinkClick,
      isMobileViewport,
      isNavigationFullyExpanded,
      navigation,
    ],
  );

  const getDrawerSharedSx = React.useCallback(
    (isMini: boolean) => {
      const drawerWidth = isMini ? 64 : 220;

      return {
        top: "80px",
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundImage: "none",
          // Ensure the permanent/left drawer sits below the AppBar. We lowered
          // the AppBar z-index earlier to allow portaled remote overlays above
          // the header; this sets the drawer paper to a value below the AppBar
          // so the sidebar won't visually overlap the header.
          zIndex: theme.zIndex.appBar - 300,
          ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        },
      };
    },
    [isNavigationExpanded],
  );

  function ToolbarActions() {
    return null;
  }

  const ToolbarActionsSlot = slots?.toolbarActions ?? ToolbarActions;
  const ToolbarAccountSlot = slots?.toolbarAccount ?? Account;

  const breadcrumbs = matches
    // only include routes that have a title
    .filter((match) => (match.handle as any)?.title)
    .map((match, index) => {
      const title = (match.handle as any)?.title;
      const isLast = index === matches.length - 1;
      return isLast ? (
        <Typography key={title} color="text.primary">
          {title}
        </Typography>
      ) : (
        <LinkMUI
          key={title}
          component={RouterLink}
          to={match.pathname}
          underline="hover"
          color="inherit"
        >
          {title}
        </LinkMUI>
      );
    });

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={(theme: Theme) => ({
          background:
            "linear-gradient(135deg, #00263A 0%, #003d5c 50%, #004d73 100%)",
          borderBottom: "4px solid transparent",
          borderImage:
            "linear-gradient(90deg, transparent 10%, #44D62C 50%, transparent 90%) 1",
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
          boxShadow:
            "0 4px 10px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(68, 214, 44, 0.15)",
          backdropFilter: "blur(8px)",
          // Lower the AppBar z-index slightly so portaled overlays (modals/drawers)
          // from child/microfrontends can render above the header. This is a
          // non-invasive host-side fix to avoid changing remote apps.
          zIndex: theme.zIndex.appBar - 200,
        })}
      >
        <Toolbar
          sx={{
            background: "transparent",
            minWidth: "100%",
            height: "60px !important",
            minHeight: "60px !important",
            mx: { xs: -0.75, sm: -1.5 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            {!disableCollapsibleSidebar && (
              <div
                onMouseEnter={() => {
                  if (!isDashboard) setIsDesktopNavigationExpanded(true);
                }}
                onMouseLeave={() => {
                  if (!isDashboard) setIsDesktopNavigationExpanded(false);
                }}
              >
                {isDesktopNavigationExpanded ? (
                  <MenuOpenIcon
                    sx={{ color: "#FFF", height: "25px", width: "25px" }}
                  />
                ) : (
                  <MenuIcon
                    sx={{ color: "#FFF", height: "25px", width: "25px" }}
                  />
                )}
              </div>
            )}
            <Link href="/" style={{ color: "inherit", textDecoration: "none" }}>
              <LogoContainer>{branding?.logo}</LogoContainer>
            </Link>
          </Stack>

          {/* Center: Page title */}
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#FFF" }}>
              {currentPageName || "Portal"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <ToolbarActionsSlot {...slotProps?.toolbarActions} />
            <ToolbarAccountSlot {...slotProps?.toolbarAccount} />
          </Stack>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={getDrawerSharedSx(!isDashboard && !isDesktopNavigationExpanded)}
        onMouseEnter={() => {
          if (!isDashboard) setIsDesktopNavigationExpanded(true);
        }}
        onMouseLeave={() => {
          if (!isDashboard) setIsDesktopNavigationExpanded(false);
        }}
      >
        {getDrawerContent(!isDashboard && !isDesktopNavigationExpanded)}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: {
            xs:
              disableCollapsibleSidebar && isNavigationExpanded
                ? "100%"
                : "auto",
            md: "auto",
          },
          m: "0px",
          mt: "80px",

          // ✅ NEW: Prevent extra spacing inside from MUI baseline or child elements
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch", // Ensures full width usage
          justifyContent: "flex-start", // ⬅️ stops vertical centering
          boxSizing: "border-box",
        }}
      >
        {/* <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          {breadcrumbs}
        </Breadcrumbs> */}
        <Outlet />
      </Box>
    </Box>
  );
}

export { DashboardLayout };
